// api/chamadaSenha/index.ts

import type { $Fetch } from "ofetch";
import type { ChamadaSenha } from "../../types/chamada-senha";
import type { statusAgendamento } from "../../types/chamada-senha";
/**
 * Nome do Controller da API
 */
const controller = "/chamadasenha";
const getTokenPin = () => localStorage.getItem("tokenPin") ?? "";
// const controller = "/TelaoMdw";

/**
 * Classe de gerenciamento de chamadas da API de chamada de senha
 *
 * @class
 */
export default class ChamadaSenhaApi {
  /**
   * Instância da dependência da API (ofetch)
   */
  private api;

  /**
   * Cria uma nova instância da classe ChamadaSenhaApi.
   * @constructor
   * @param api - Instância da dependência da API (ofetch).
   */
  constructor(api: $Fetch) {
    this.api = api;
  }

  /**
   * Busca a lista de telões cadastrados
   * @returns Lista de telões cadastrados ou null se houver erro
   */

  async buscarListaTelao() {
    try {
      const resp = await this.api<any>(
        `${controller}/ListarTeloesCadastrados`,
        {
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao buscar a lista de telões:", error);
      return null;
    }
  }

  /**
   * Busca a menor senha no banco.
   * @param codTipoSenha - Código do tipo da senha.
   * @param codTelao - Código do telão.
   * @returns Retorna a menor senha encontrada.
   */
  async buscarMenorSenhaBanco(codTipoSenha: number, codTelao: number) {
    try {
      const resp = await this.api(
        `${controller}/BuscarMenorSenhaBanco?codTipoSenha=${codTipoSenha}&codTelao=${codTelao}  `,
        {
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao buscar menor senha no banco:", error);
      throw error;
    }
  }

  /**
   * Busca as senhas chamadas no dia para um determinado telão.
   * @param codTelao - Código do telão.
   * @returns Lista de senhas chamadas no dia.
   */
  async buscarSenhasChamadasNoDia(codTelao: number) {
    try {
      const resp = await this.api(`${controller}/BuscarSenhasChamadasNoDia`, {
        method: "GET",
        params: { codTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao buscar senhas chamadas no dia:", error);
      throw error;
    }
  }

  /**
   * Busca todas as senhas chamadas no dia.
   * @returns Lista de todas as senhas chamadas no dia.
   */
  async buscarTodasAsSenhasChamadasNoDia() {
    try {
      const resp = await this.api(
        `${controller}/BuscarTodasAsSenhasChamadasNoDia`,
        {
          method: "GET",
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao buscar todas as senhas chamadas no dia:", error);
      throw error;
    }
  }

  /**
   * Busca a maior senha no banco para um determinado tipo de senha.
   * @param codTipoSenha - Código do tipo da senha.
   * @returns Retorna a maior senha encontrada.
   */
  async buscarMaiorSenhaBancoTotem(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/BuscarMaiorSenhaBancoTotem`, {
        method: "GET",
        params: { codTipoSenha },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao buscar maior senha no banco:", error);
      throw error;
    }
  }

  /**
   * Gera uma nova senha no totem.
   * @body codTipoSenha - Código do tipo da senha.
   * @returns Retorna a senha gerada.
   */
  async gerarSenhaTotem(codTipoSenha: number) {
    try {
      // Remova o codTipoSenha da URL e envie-o somente no corpo da requisição
      const resp = await this.api(
        `${controller}/GerarSenhaTotem`, // URL sem query string
        {
          headers: {
            "Content-Type": "application/json", // Inclua o cabeçalho correto
            AuthorizationPin: getTokenPin(),
          }, // Enviando no corpo da requisição como JSON
          method: "POST",
          body: { codTipoSenha }, // Envia o número diretamente
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao gerar senha no totem:", error);
      throw error;
    }
  }

  /**
   * Gera uma nova senha no totem.
   * @body codTipoSenha - Código do tipo da senha.
   * @returns Retorna a senha gerada.
   */
  async gerarSenhaTotemComAgendamento(
    codTipoSenha: number,
    codAgendamento: number,
  ) {
    try {
      // Remova o codTipoSenha da URL e envie-o somente no corpo da requisição
      const resp = await this.api(
        `${controller}/GerarSenhaTotemComAgendamento`, // URL sem query string
        {
          headers: {
            "Content-Type": "application/json", // Inclua o cabeçalho correto
            AuthorizationPin: getTokenPin(),
          }, // Enviando no corpo da requisição como JSON
          method: "POST",
          body: { codTipoSenha, codAgendamento }, // Envia o número diretamente
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao gerar senha no totem:", error);
      throw error;
    }
  }
  /**
   * Inativa uma senha chamada.
   * @param codSenha - Código da senha.
   * @param estacao - Nome da estação.
   * @returns Retorna o resultado da operação.
   */
  async inativarSenhaChamada(codSenha: number, estacao: string) {
    try {
      const resp = await this.api(`${controller}/InativarSenhaChamada`, {
        method: "POST",
        body: {
          codSenha: codSenha,
          estacao: estacao,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao inativar senha chamada:", error);
      throw error;
    }
  }

  /**
   * Lista os telões cadastrados
   * @param codTipoSenha - Código do tipo de senha
   * @returns Lista de telões cadastrados
   */
  async listarTeloesCadastrados(codTipoSenha: number) {
    try {
      const resp = await this.api(`${controller}/ListarTeloesCadastrados`, {
        method: "GET",
        params: {
          codTipoSenha,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao listar telões cadastrados:", error);
      throw error;
    }
  }

  async ObterStatusCustomizado() {
    try {
      const resp = await this.api(`${controller}/ObterStatusCustomizado`, {
        headers: {
          "Content-Type": "application/json", // Inclua o cabeçalho correto
          AuthorizationPin: getTokenPin(),
        },
        method: "GET",
      });
      return resp;
    } catch (error) {
      console.error("Erro ao obter status customizado:", error);
      throw error;
    }
  }
  async DefinirStatusCustomizado(statusAgendamento: statusAgendamento) {
    try {
      const resp = await this.api(`${controller}/DefinirStatusCustomizado`, {
        headers: {
          "Content-Type": "application/json", // Inclua o cabeçalho correto
          AuthorizationPin: getTokenPin(),
        },
        method: "PUT",
        body: statusAgendamento,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao definir status customizado:", error);
      throw error;
    }
  }

  async ListarProximo(codTelao: number) {
    try {
      const resp = await this.api(`${controller}/ListarProximo`, {
        headers: {
          "Content-Type": "application/json", // Inclua o cabeçalho correto
          AuthorizationPin: getTokenPin(),
        }, // Enviando no corpo da requisição como JSON
        method: "GET",
        params: { codTelao },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao chamar senhas:", error);
      throw error;
    }
  }

  /**
   * Lista senhas aguardando chamada para um determinado telão.
   * @param codTelao - Código do telão.
   * @returns Retorna a lista de senhas aguardando chamada.
   */

  async listarSenhasAguardandoChamada(codTelao: number) {
    try {
      const resp = await this.api(
        `${controller}/ListarSenhasAguardandoChamada`,
        {
          method: "GET",
          params: { codTelao },
        },
      );
      return resp;
    } catch (error) {
      console.error("Erro ao listar senhas aguardando chamada:", error);
      throw error;
    }
  }

  /**
   * Salva uma nova chamada de senha.
   * @param chamadaSenha - Objeto contendo as informações da chamada de senha.
   * @returns Retorna o resultado da operação.
   */
  async salvar(chamadaSenha: ChamadaSenha) {
    try {
      const resp = await this.api(`${controller}/Salvar`, {
        method: "POST",
        body: chamadaSenha,
      });
      return resp;
    } catch (error) {
      console.error("Erro ao salvar chamada de senha:", error);
      throw error;
    }
  }

  async ChamarTexto(codTelao: number, textoChamada: string) {
    try {
      const resp = await this.api(`${controller}/postChamartexto`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          AuthorizationPin: getTokenPin(),
        },
        body: {
          codTelao,
          textoChamada,
        },
      });

      return resp;
    } catch (error) {
      console.error("Erro ao salvar chamada de senha:", error);
      throw error;
    }
  }

  async lerAgendamentosPorPaciente(codPaciente: number) {
    try {
      const resp = await this.api(`${controller}/lerAgendamentosPorPaciente`, {
        headers: {
          "Content-Type": "application/json",
          AuthorizationPin: getTokenPin(),
        },
        method: "GET",
        params: { codPaciente },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao salvar chamada de senha:", error);
      throw error;
    }
  }

  async pacienteTotemStatus(
    codAgendamento: number,
    codAgenda: number,
    dataHoraAgendada: string | Date,
    codUsuarioLogado: number,
    status: number,
  ) {
    try {
      const resp = await this.api(`${controller}/pacienteTotemStatus`, {
        headers: {
          "Content-Type": "application/json",
          AuthorizationPin: getTokenPin(),
        },
        method: "POST",
        params: {
          codAgendamento,
          codAgenda,
          dataHoraAgendada,
          codUsuarioLogado,
          status,
        },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao salvar chamada de senha:", error);
      throw error;
    }
  }
  async BuscarCPF(CPF: string) {
    try {
      const resp = await this.api(`${controller}/BuscarCPF`, {
        headers: {
          "Content-Type": "application/json",
          AuthorizationPin: getTokenPin(),
        },
        method: "GET",
        params: { CPF },
      });
      return resp;
    } catch (error) {
      console.error("Erro ao salvar chamada de senha:", error);
      throw error;
    }
  }
}
